import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { 
  collection, 
  doc, 
  getDoc, 
  getDocs, 
  query, 
  where, 
  updateDoc,
  addDoc,
  Timestamp,
  writeBatch,
  orderBy,
  serverTimestamp,
  limit
} from 'firebase/firestore';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faSpinner, 
  faClipboardCheck, 
  faQrcode, 
  faList,
  faCheckCircle, 
  faTimesCircle, 
  faChevronDown, 
  faChevronRight,
  faSignInAlt,
  faSignOutAlt,
  faSearch,
  faFilter,
  faChevronLeft,
  faHandHolding,
  faInbox,
  faPlus,
  faMinus,
  faPaperPlane,
  faCheck,
  faBan,
  faArrowsAltH,
  faShare,
  faTimes,
} from '@fortawesome/free-solid-svg-icons';
import { UserContext } from '../../context/UserContext';
import { TourContext } from '../../context/TourContext';
import { getFirestoreInstance } from '../../utils/firebaseHelper';
import QrScanner from '../common/QrScanner'; // Import the QR Scanner component
import styles from './AssignedInventoryTab.module.css';
import { createNotification } from '../../utils/NotificationService';

const AssignedInventoryTab = ({ initialView = 'inventory', vendorId = null, dateId }) => {
    const navigate = useNavigate();
    const { userData } = useContext(UserContext);
    const { selectedTour, selectedDate } = useContext(TourContext);
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

    // Add this to extract vendorId from URL query parameters
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const urlVendorId = queryParams.get('vendorId');
    
    // Use vendorId prop or extract from URL query parameters
    const effectiveVendorId = vendorId || urlVendorId;
  
  // State for inventory items and UI
  const [assignedInventory, setAssignedInventory] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [viewMode, setViewMode] = useState(initialView); // 'inventory', 'checklist', 'request', 'requests'
  const [checklistMode, setChecklistMode] = useState('checkIn'); // 'checkIn' or 'checkOut'
  
  // State for expanded items in checklist
  const [expandedItems, setExpandedItems] = useState({});
  
  // State for QR scanner
  const [showScanner, setShowScanner] = useState(false);
  const [scannerMode, setScannerMode] = useState('individual'); // 'individual' or 'markAll'
  const [scanSuccessMessage, setScanSuccessMessage] = useState('');
  
  // Category and search support
  const [categories, setCategories] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 12;
  const [activeCategory, setActiveCategory] = useState('Audio Equipment');
  const [hasMoreItems, setHasMoreItems] = useState(true);

  // Request-related state
  const [requestableInventory, setRequestableInventory] = useState([]);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [selectedItems, setSelectedItems] = useState({});
  const [requestNote, setRequestNote] = useState('');
  const [showRequestNoteModal, setShowRequestNoteModal] = useState(false);
  const [itemBeingRequested, setItemBeingRequested] = useState(null);
  const [isLoadingRequests, setIsLoadingRequests] = useState(false);
  const [requestSending, setRequestSending] = useState(false);
  const [showRequestSuccessMessage, setShowRequestSuccessMessage] = useState(false);
  const [requestSuccessMessage, setRequestSuccessMessage] = useState('');
  
  // New state for request information
  const [pendingItemRequests, setPendingItemRequests] = useState({});
  const [acceptedItemRequests, setAcceptedItemRequests] = useState({});
  const [isLoadingItemRequests, setIsLoadingItemRequests] = useState(false);

  const [noVendorSelected, setNoVendorSelected] = useState(false);

  const [showPdfModal, setShowPdfModal] = useState(false);
const [pdfHtml, setPdfHtml] = useState('');

  // Determine if user should see the checklist tab
  const showChecklistTab = 
    (userData?.userType === 'Vendor' && (!effectiveVendorId || effectiveVendorId === userData.userId)) || 
    userData?.userType === 'Staff';
  
  // Determine if user should see the request tab (everyone except Vendor and Staff)
  const showRequestTab = 
    userData?.userType !== 'Vendor' && 
    userData?.userType !== 'Staff' && 
    userData?.userType !== 'Organization';
  
  // Determine if user should see the requests tab (only Vendor and Staff)
  const showRequestsTab = 
    userData?.userType === 'Vendor' || 
    userData?.userType === 'Staff'; 
  
  // Set view mode when initialView prop changes
  useEffect(() => {
    if (initialView === 'checklist' && !showChecklistTab) {
      // Fallback to inventory view if user can't access checklist
      setViewMode('inventory');
    } else if (initialView === 'request' && !showRequestTab) {
      // Fallback to inventory view if user can't access request
      setViewMode('inventory');
    } else if (initialView === 'requests' && !showRequestsTab) {
      // Fallback to inventory view if user can't access requests
      setViewMode('inventory');
    } else {
      setViewMode(initialView);
    }
  }, [initialView, showChecklistTab, showRequestTab, showRequestsTab]);
  
  
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (!selectedTour?.id || !selectedDate || !userData?.userId) {
      setIsLoading(false);
      return;
    }
    
    const fetchAssignedInventory = async () => {
      try {
        setIsLoading(true);
        
        // Reset states when starting a new fetch
        if (searchTerm.trim() === '') {
          setAssignedInventory([]);
          
          setHasMoreItems({
            audioEquipment: true,
            backlineEquipment: true,
            lightingEquipment: true,
            visualEquipment: true,
            pyroEquipment: true,
            stagingEquipment: true,
            miscellaneousEquipment: true
          });
        }
        
        const db = getFirestoreInstance();
        
        // Get the assignedInventoryGroups subcollection
        const groupsCollectionRef = collection(
          db, 
          'Tours', 
          selectedTour.id, 
          'tourDates', 
          selectedDate,
          'assignedInventoryGroups'
        );
        
        // Create a query to filter by vendorId and inventoryType
        let groupsQuery;
        if (effectiveVendorId) {
          groupsQuery = query(
            groupsCollectionRef,
            where('vendorId', '==', effectiveVendorId),
            where('inventoryType', '==', activeCategory)
          );
        } else if (userData.userType === 'Vendor') {
          // Vendors can only see their own inventory
          groupsQuery = query(
            groupsCollectionRef,
            where('vendorId', '==', userData.userId),
            where('inventoryType', '==', activeCategory)
          );
        } else {
          // Staff and Organization can see all inventory for the selected category
          groupsQuery = query(
            groupsCollectionRef,
            where('inventoryType', '==', activeCategory)
          );
        }
        
        const groupsSnapshot = await getDocs(groupsQuery);
          
        if (groupsSnapshot.empty) {
          setAssignedInventory([]);
          setIsLoading(false);
          setHasMoreItems(false);
          return;
        }
        
        // Convert snapshot to array of inventory groups
        let inventoryGroups = groupsSnapshot.docs.map(doc => ({
          groupId: doc.id,
          ...doc.data()
        }));
        
        // Paginate the results
        const totalItems = inventoryGroups.length;
        setHasMoreItems(totalItems > currentPage * ITEMS_PER_PAGE);
        
        // Only show the items for the current page
        inventoryGroups = inventoryGroups.slice(0, currentPage * ITEMS_PER_PAGE);
        
        // Extract categories from inventory groups for the filter
        const categorySet = new Set();
        inventoryGroups.forEach(item => {
          if (item.inventoryType) {
            categorySet.add(item.inventoryType);
          }
        });
        setCategories(Array.from(categorySet));
        
        // Enhance inventory data with check-in/out information and correct counts
        const enhancedInventory = await Promise.all(
          inventoryGroups.map(async group => {
            // First, get all individual items assigned to this group for the tour date
            const itemsCollectionRef = collection(
              db,
              'Tours',
              selectedTour.id,
              'tourDates',
              selectedDate,
              'assignedInventoryItems'
            );
            
            const assignedItemsQuery = query(
              itemsCollectionRef,
              where('groupId', '==', group.groupId)
            );
            
            const assignedItemsSnapshot = await getDocs(assignedItemsQuery);
            const assignedItems = assignedItemsSnapshot.docs.map(doc => ({
              id: doc.id,
              ...doc.data()
            }));
            
            // Now fetch the corresponding items from the vendor's inventory to get full details
            const vendorItemsQuery = query(
              collection(db, 'users', group.vendorId, 'inventoryItems'),
              where('groupId', '==', group.groupId)
            );
            
            const vendorItemsSnap = await getDocs(vendorItemsQuery);
            
            // Map vendor items to assigned items where possible
            const individualItems = assignedItems.map(assignedItem => {
              // Find the corresponding vendor item
              const vendorItem = vendorItemsSnap.docs.find(doc => doc.id === assignedItem.id);
              
              if (vendorItem) {
                const vendorItemData = vendorItem.data();
                return {
                  id: assignedItem.id,
                  ...vendorItemData,
                  // Use the latest check-in/out status from either source
                  checkInStatus: assignedItem.checkInStatus || vendorItemData.checkInStatus || {
                    isCheckedIn: false,
                    checkedInBy: null,
                    checkedInByName: null,
                    checkedInAt: null
                  },
                  checkOutStatus: assignedItem.checkOutStatus || vendorItemData.checkOutStatus || {
                    isCheckedOut: false,
                    checkedOutBy: null,
                    checkedOutByName: null,
                    checkedOutAt: null
                  }
                };
              } else {
                // If vendor item not found, use just the assigned item data
                return {
                  id: assignedItem.id,
                  ...assignedItem,
                  // Initialize check status if not present
                  checkInStatus: assignedItem.checkInStatus || {
                    isCheckedIn: false,
                    checkedInBy: null,
                    checkedInByName: null,
                    checkedInAt: null
                  },
                  checkOutStatus: assignedItem.checkOutStatus || {
                    isCheckedOut: false,
                    checkedOutBy: null,
                    checkedOutByName: null,
                    checkedOutAt: null
                  }
                };
              }
            });
            
            // Calculate check-in/out quantities
            const checkedInCount = individualItems.filter(item => 
              item.checkInStatus && item.checkInStatus.isCheckedIn
            ).length;
            
            const checkedOutCount = individualItems.filter(item => 
              item.checkOutStatus && item.checkOutStatus.isCheckedOut
            ).length;
            
            // Use the correct assigned quantity from the group document
            // Fall back to the count of individual items if totalQuantity is not available
            const assignedQuantity = group.totalQuantity || individualItems.length;
            
            return {
              ...group,
              individualItems,
              assignedQuantity, // Store the correct assigned quantity
              quantityCheckedIn: checkedInCount,
              quantityCheckedOut: checkedOutCount,
              lastUpdated: individualItems.reduce((latest, item) => {
                const inTime = item.checkInStatus?.checkedInAt ? new Date(item.checkInStatus.checkedInAt) : null;
                const outTime = item.checkOutStatus?.checkedOutAt ? new Date(item.checkOutStatus.checkedOutAt) : null;
                
                let itemLatest = null;
                if (inTime && outTime) {
                  itemLatest = inTime > outTime ? inTime : outTime;
                } else {
                  itemLatest = inTime || outTime;
                }
                
                if (!latest) return itemLatest;
                if (!itemLatest) return latest;
                return itemLatest > latest ? itemLatest : latest;
              }, null)
            };
          })
        );
        
        setAssignedInventory(enhancedInventory);
      } catch (error) {
        console.error('Error fetching assigned inventory:', error);
        setAssignedInventory([]);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchAssignedInventory();
  }, [selectedTour, selectedDate, userData, effectiveVendorId, activeCategory, currentPage]);
  
  // Fetch existing requests for items
  const fetchExistingRequests = useCallback(async () => {
    if (!selectedTour?.id || !selectedDate) return;
    
    try {
      setIsLoadingItemRequests(true);
      const db = getFirestoreInstance();
      
      // Get all requests for this tour date
      const requestsRef = collection(
        db, 
        'Tours', 
        selectedTour.id, 
        'tourDates', 
        selectedDate,
        'inventoryRequests'
      );
      
      // Get both pending and approved requests
      const requestsSnapshot = await getDocs(requestsRef);
      
      if (requestsSnapshot.empty) {
        setPendingItemRequests({});
        setAcceptedItemRequests({});
        setIsLoadingItemRequests(false);
        return;
      }
      
      // Process requests by groupId
      const pendingReqs = {};
      const acceptedReqs = {};
      
      for (const doc of requestsSnapshot.docs) {
        const request = doc.data();
        const groupId = request.groupId;
        
        // Get user details
        let userData = null;
        if (request.requestedBy) {
          try {
            const userDoc = await getDoc(doc(db, 'users', request.requestedBy));
            if (userDoc.exists()) {
              userData = userDoc.data();
            }
          } catch (err) {
            console.error("Error fetching requester details:", err);
          }
        }
        
        const requestInfo = {
          id: doc.id,
          quantity: request.requestedQuantity,
          requestedBy: request.requestedBy,
          requesterName: userData?.fullName || request.requesterDetails?.fullName || 'Unknown User',
          status: request.status,
          timestamp: request.createdAt,
          notes: request.notes
        };
        
        // Add to appropriate collection based on status
        if (request.status === 'pending') {
          if (!pendingReqs[groupId]) pendingReqs[groupId] = [];
          pendingReqs[groupId].push(requestInfo);
        } else if (request.status === 'approved') {
          if (!acceptedReqs[groupId]) acceptedReqs[groupId] = [];
          acceptedReqs[groupId].push(requestInfo);
        }
      }
      
      setPendingItemRequests(pendingReqs);
      setAcceptedItemRequests(acceptedReqs);
    } catch (error) {
      console.error('Error fetching existing requests:', error);
    } finally {
      setIsLoadingItemRequests(false);
    }
  }, [selectedTour, selectedDate]);
  
  // Fetch all requestable inventory (for the request tab)
  useEffect(() => {
    if (!selectedTour?.id || !selectedDate || !userData?.userId || viewMode !== 'request') {
      return;
    }
    
    fetchRequestableInventory();
  }, [selectedTour, selectedDate, userData, viewMode, activeCategory]);
  
  // Modified requestable inventory fetch function
  const fetchRequestableInventory = async () => {
    if (!selectedTour?.id || !selectedDate || !userData?.userId || viewMode !== 'request') {
      return;
    }
    
    // Check if a vendor is selected
    if (!effectiveVendorId) {
      setNoVendorSelected(true);
      setRequestableInventory([]);
      setIsLoading(false);
      return;
    } else {
      setNoVendorSelected(false);
    }
    
    try {
      setIsLoading(true);
      
      const db = getFirestoreInstance();
      
      // First, get the tour document to check vendorDetails
      const tourRef = doc(db, 'Tours', selectedTour.id);
      const tourSnap = await getDoc(tourRef);
      
      if (!tourSnap.exists()) {
        setRequestableInventory([]);
        setIsLoading(false);
        return;
      }
      
      const tourData = tourSnap.data();
      const vendorDetails = tourData.vendorDetails || [];
      
      // Find the vendor in the tour's vendorDetails
      const selectedVendor = vendorDetails.find(vendor => vendor.userId === effectiveVendorId);
      
      if (!selectedVendor) {
        console.log("Selected vendor not found in tour");
        setRequestableInventory([]);
        setIsLoading(false);
        return;
      }
      
      // Check if the vendor is assigned to this specific tour date
      const isAssignedToThisDate = selectedVendor.assignedDates?.some(
        dateInfo => dateInfo.tourDateDocId === selectedDate && dateInfo.status === 'accepted'
      );
      
      if (!isAssignedToThisDate) {
        console.log("Selected vendor is not assigned to this specific tour date");
        setRequestableInventory([]);
        setIsLoading(false);
        return;
      }
      
      console.log(`Vendor ${effectiveVendorId} is assigned to tour date ${selectedDate}, fetching inventory`);
      
      // Get existing assigned inventory IDs for this tour date
      const assignedGroupsRef = collection(
        db, 
        'Tours', 
        selectedTour.id, 
        'tourDates', 
        selectedDate,
        'assignedInventoryGroups'
      );
      
      // Filter by vendor ID to find already assigned items
      const assignedGroupsQuery = query(
        assignedGroupsRef,
        where('vendorId', '==', effectiveVendorId)
      );
      
      const assignedGroupsSnap = await getDocs(assignedGroupsQuery);
      const assignedGroupIds = assignedGroupsSnap.docs.map(doc => doc.id);
      
      // Get all inventory groups for this vendor
      const vendorInventoryRef = collection(db, 'users', effectiveVendorId, 'inventoryGroups');
      let inventoryQuery;
      
      if (activeCategory && activeCategory !== 'all') {
        inventoryQuery = query(
          vendorInventoryRef,
          where('inventoryType', '==', activeCategory)
        );
      } else {
        inventoryQuery = vendorInventoryRef;
      }
      
      const inventorySnap = await getDocs(inventoryQuery);
      console.log(`Found ${inventorySnap.size} inventory groups for vendor ${effectiveVendorId}`);
      
      // Process all inventory groups
      const allInventory = [];
      
      for (const doc of inventorySnap.docs) {
        const groupData = doc.data();
        
        // Check if this group is already assigned to this tour date
        const isAssigned = assignedGroupIds.includes(doc.id);
        
        // Add to inventory list regardless of availability
        allInventory.push({
          id: doc.id,
          groupId: doc.id,
          ...groupData,
          vendorId: effectiveVendorId,
          isAssigned
        });
      }
      
      console.log(`Total requestable inventory items: ${allInventory.length}`);
      
      // Set the requestable inventory
      setRequestableInventory(allInventory);
      
      // Extract categories from inventory
      const categorySet = new Set();
      allInventory.forEach(item => {
        if (item.inventoryType) {
          categorySet.add(item.inventoryType);
        }
      });
      setCategories(Array.from(categorySet));
      
      // Also fetch existing requests
      fetchExistingRequests();
    } catch (error) {
      console.error('Error fetching requestable inventory:', error);
      setRequestableInventory([]);
    } finally {
      setIsLoading(false);
    }
  };
  
  // Fetch pending requests (for the requests tab)
useEffect(() => {
  if (!selectedTour?.id || !selectedDate || !userData?.userId || viewMode !== 'requests') {
    return;
  }
  
  const fetchPendingRequests = async () => {
    try {
      setIsLoadingRequests(true);
      
      const db = getFirestoreInstance();
      
      // Query requests that are pending for this tour date
      const requestsRef = collection(
        db, 
        'Tours', 
        selectedTour.id, 
        'tourDates', 
        selectedDate,
        'inventoryRequests'
      );
      
      let requestsQuery;
      
      if (userData.userType === 'Vendor') {
        // Vendors only see requests for their inventory
        requestsQuery = query(
          requestsRef,
          where('vendorId', '==', userData.userId),
          where('status', '==', 'pending'),
          orderBy('createdAt', 'desc')
        );
      } else {
        // Staff and Organization see all requests
        requestsQuery = query(
          requestsRef,
          where('status', '==', 'pending'),
          orderBy('createdAt', 'desc')
        );
      }
      
      const requestsSnap = await getDocs(requestsQuery);
      
      if (requestsSnap.empty) {
        setPendingRequests([]);
        setIsLoadingRequests(false);
        return;
      }
      
      // Fetch the user details for each request
      // THE FIX: Change 'doc' to 'docSnapshot' to avoid shadowing the 'doc' function
      const requestsWithUserDetails = await Promise.all(
        requestsSnap.docs.map(async (docSnapshot) => {
          const requestData = docSnapshot.data();
          
          // Get user details
          let userData = null;
          if (requestData.requestedBy) {
            // Fixed: Use doc function without shadowing
            const userDoc = await getDoc(doc(db, 'users', requestData.requestedBy));
            if (userDoc.exists()) {
              userData = userDoc.data();
            }
          }
          
          return {
            id: docSnapshot.id,
            ...requestData,
            userData: userData ? {
              fullName: userData.fullName || 'Unknown User',
              userName: userData.userName || '',
              profilePictureUrl: userData.profilePictureUrl || '',
              userType: userData.userType || ''
            } : null
          };
        })
      );
      
      setPendingRequests(requestsWithUserDetails);
    } catch (error) {
      console.error('Error fetching pending requests:', error);
      setPendingRequests([]);
    } finally {
      setIsLoadingRequests(false);
    }
  };
  
  fetchPendingRequests();
}, [selectedTour, selectedDate, userData, viewMode]);
  
  // Add load more function
  const handleLoadMore = () => {
    setCurrentPage(prevPage => prevPage + 1);
  };


  const ChecklistPdfModal = ({ isOpen, onClose, pdfHtml }) => {
    if (!isOpen) return null;
  
    const handlePrint = () => {
      // Create a hidden iframe for printing
      const printFrame = document.createElement('iframe');
      printFrame.style.display = 'none';
      document.body.appendChild(printFrame);
      
      printFrame.contentDocument.write(pdfHtml);
      printFrame.contentDocument.close();
      
      // Use setTimeout to avoid blocking
      setTimeout(() => {
        printFrame.contentWindow.print();
        // Remove iframe after a delay
        setTimeout(() => {
          document.body.removeChild(printFrame);
        }, 100);
      }, 100);
    };
  
    return (
      <div className={styles.modalOverlay}>
        <div className={styles.pdfModalContent}>
          <div className={styles.pdfModalHeader}>
            <h2>Checklist Preview</h2>
            <div className={styles.pdfModalActions}>
              <button className={styles.printButton} onClick={handlePrint}>
                <FontAwesomeIcon icon={faShare} />
                Print
              </button>
              <button className={styles.closeButton} onClick={onClose}>
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
          </div>
          <div className={styles.pdfModalBody}>
            <iframe
              srcDoc={pdfHtml}
              className={styles.pdfIframe}
              title="Checklist Preview"
            />
          </div>
        </div>
      </div>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
        year: 'numeric'
      });
    } catch (error) {
      return dateString;
    }
  };

  const generateChecklistHtml = () => {
    // Access tour context directly (or adjust based on your component)
    const tourDate = selectedTour?.tourDates?.find(date => date.id === dateId);
    const formattedDate = tourDate?.date ? formatDate(tourDate.date) : 'N/A';
    const mode = checklistMode === 'checkIn' ? 'Check In' : 'Check Out';
    
    // Get the items to display (use the getFilteredInventory function that's already in your component)
    const items = getFilteredInventory();
    
    const rows = items
      .map((group) => {
        const itemRows = [];
        
        // Add group row
        itemRows.push(`
          <tr class="group-row">
            <td colspan="5" style="background-color: #f8f9fa; font-weight: bold; text-align: left;">
              ${group.name || 'Unnamed Group'} (${group.brand || ''} ${group.model || ''})
            </td>
          </tr>
        `);
        
        // Add individual item rows
        if (group.individualItems && group.individualItems.length > 0) {
          group.individualItems.forEach((item) => {
            const isChecked = checklistMode === 'checkIn' 
              ? (item.checkInStatus?.isCheckedIn || false)
              : (item.checkOutStatus?.isCheckedOut || false);
            
            const timestamp = checklistMode === 'checkIn'
              ? (item.checkInStatus?.checkedInAt ? formatTimestamp(item.checkInStatus.checkedInAt) : 'N/A')
              : (item.checkOutStatus?.checkedOutAt ? formatTimestamp(item.checkOutStatus.checkedOutAt) : 'N/A');
            
            const userName = checklistMode === 'checkIn'
              ? getUserName(item.checkInStatus)
              : getUserName(item.checkOutStatus);
            
            itemRows.push(`
              <tr>
                <td style="padding-left: 30px;">${item.serialNumber || `Unit ${item.id.slice(-4)}`}</td>
                <td>${isChecked ? '✓' : ''}</td>
                <td>${timestamp}</td>
                <td>${userName}</td>
                <td></td>
              </tr>
            `);
          });
        }
        
        return itemRows.join('');
      })
      .join('');
  
    return `
      <html>
        <head>
          <style>
            @page {
              size: A4 portrait;
              margin: 15mm;
            }
            body {
              font-family: Arial, sans-serif;
              font-size: 12px;
            }
            h1, h2, h3, h4 {
              text-align: center;
              margin: 10px 0;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              margin-top: 20px;
            }
            th, td {
              border: 1px solid black;
              padding: 8px;
              text-align: left;
            }
            th {
              background-color: #f2f2f2;
              font-weight: bold;
              text-align: center;
            }
            .group-row td {
              background-color: #f8f9fa;
            }
            .summary {
              margin-top: 20px;
              border: 1px solid #ddd;
              padding: 10px;
              background-color: #f9f9f9;
            }
            .check-mark {
              color: green;
              font-weight: bold;
            }
          </style>
        </head>
        <body>
          <h1>${selectedTour?.tourName || 'Tour Inventory'}</h1>
          <h2>${selectedTour?.actDetails?.name || ''}</h2>
          <h3>${formattedDate}</h3>
          <h4>${tourDate?.event?.venueDetails?.venueData?.displayName} ${tourDate?.event?.venueDetails?.venueData?.formattedAddress}</h4>

          <h2>${mode} Checklist</h2>
          <table>
            <thead>
              <tr>
                <th>Item Name</th>
                <th>${mode}</th>
                <th>${checklistMode === 'checkIn' ? 'Checked In At' : 'Checked Out At'}</th>
                <th>${checklistMode === 'checkIn' ? 'Checked In By' : 'Checked Out By'}</th>
                <th>Notes</th>
              </tr>
            </thead>
            <tbody>
              ${rows}
            </tbody>
          </table>
          <div class="summary">
            <p><strong>Generated:</strong> ${new Date().toLocaleString()}</p>
            <p><strong>Generated By:</strong> ${userData?.fullName || 'User'}</p>
          </div>
        </body>
      </html>
    `;
  };
  
  // 4. Add this function to handle the print button click:
  const handleExportChecklist = async () => {
    try {
      const html = generateChecklistHtml();
      setPdfHtml(html);
      setShowPdfModal(true);
    } catch (error) {
      console.error('Error exporting checklist:', error);
      alert('Failed to export checklist. Please try again.');
    }
  };
  

  const handleBack = () => {
    // Check if we have a fromEvent flag in location state
    if (location.state?.fromEvent) {
      // If coming from vendor section in Event, go back to event tab
      navigate(`/tour/${selectedTour?.id}/${selectedDate}`, {
        state: { activeTab: 'event' }
      });
    } else {
      // Otherwise use the previous path if available, or go to the default path
      const prevPath = location.state?.previousPath;
      if (prevPath) {
        navigate(prevPath);
      } else {
        navigate(`/tour/${selectedTour?.id}/${selectedDate}`);
      }
    }
  };

  // Handle toggling item expansion in checklist
  const toggleItemExpansion = (groupId) => {
    setExpandedItems(prev => ({
      ...prev,
      [groupId]: !prev[groupId]
    }));
  };
  
  // Handle marking an individual item as checked in/out
  const handleMarkItem = async (groupId, itemId, action, userFullName = null) => {
    try {
      const db = getFirestoreInstance();
      const group = assignedInventory.find(g => g.groupId === groupId);
      
      if (!group) return false;
      
      // Check if item is already checked
      const item = group.individualItems.find(i => i.id === itemId);
      if ((action === 'checkIn' && item.checkInStatus?.isCheckedIn) || 
          (action === 'checkOut' && item.checkOutStatus?.isCheckedOut)) {
        // Item is already checked, don't allow re-checking
        return false;
      }
      
      const timestamp = Timestamp.now();
      
      // Get user's name if not provided
      const userName = userFullName || userData?.fullName || 'Unknown User';
      
      // Update only the assignedInventoryItems subcollection in the tour date
      const assignedItemRef = doc(
        db, 
        'Tours', 
        selectedTour.id, 
        'tourDates', 
        selectedDate,
        'assignedInventoryItems',
        itemId
      );
      
      const updateData = action === 'checkIn' 
        ? {
            checkInStatus: {
              isCheckedIn: true,
              checkedInBy: userData.userId,
              checkedInByName: userName,
              checkedInAt: timestamp
            }
          }
        : {
            checkOutStatus: {
              isCheckedOut: true,
              checkedOutBy: userData.userId,
              checkedOutByName: userName,
              checkedOutAt: timestamp
            }
          };
      
      await updateDoc(assignedItemRef, updateData);
      
      // Update local state
      setAssignedInventory(prevInventory => 
        prevInventory.map(g => {
          if (g.groupId !== groupId) return g;
          
          const updatedItems = g.individualItems.map(item => {
            if (item.id !== itemId) return item;
            
            if (action === 'checkIn') {
              return {
                ...item,
                checkInStatus: {
                  isCheckedIn: true,
                  checkedInBy: userData.userId,
                  checkedInByName: userName,
                  checkedInAt: timestamp
                }
              };
            } else {
              return {
                ...item,
                checkOutStatus: {
                  isCheckedOut: true,
                  checkedOutBy: userData.userId,
                  checkedOutByName: userName,
                  checkedOutAt: timestamp
                }
              };
            }
          });
          
          // Update count of checked in/out items
          const checkedInCount = updatedItems.filter(item => 
            item.checkInStatus && item.checkInStatus.isCheckedIn
          ).length;
          
          const checkedOutCount = updatedItems.filter(item => 
            item.checkOutStatus && item.checkOutStatus.isCheckedOut
          ).length;
          
          return {
            ...g,
            individualItems: updatedItems,
            quantityCheckedIn: checkedInCount,
            quantityCheckedOut: checkedOutCount,
            lastUpdated: timestamp
          };
        })
      );
  
      // Set success message
      const itemName = group.individualItems.find(i => i.id === itemId)?.serialNumber || `Item ID: ${itemId.slice(-6)}`;
      setScanSuccessMessage(`Successfully ${action === 'checkIn' ? 'checked in' : 'checked out'} ${itemName}`);
      
      // Clear message after 3 seconds
      setTimeout(() => {
        setScanSuccessMessage('');
      }, 3000);
      
      return true; // Return success
    } catch (error) {
      console.error(`Error marking item as ${action === 'checkIn' ? 'checked in' : 'checked out'}:`, error);
      return false; // Return failure
    }
  };
  
  // Handle marking all items in a group
  const handleMarkAllItems = async (groupId, action) => {
    try {
      const db = getFirestoreInstance();
      const group = assignedInventory.find(g => g.groupId === groupId);
      
      if (!group) return false;
      
      const timestamp = Timestamp.now();
      const batch = writeBatch(db);
      
      // Get user's name
      const userName = userData?.fullName || 'Unknown User';
      
      // Only apply to items that aren't already marked
      const itemsToUpdate = group.individualItems.filter(item => 
        (action === 'checkIn' && !item.checkInStatus?.isCheckedIn) || 
        (action === 'checkOut' && !item.checkOutStatus?.isCheckedOut)
      );
      
      if (itemsToUpdate.length === 0) {
        return false; // Nothing to update
      }
      
      // Update each item in the tour date's assignedInventoryItems subcollection
      itemsToUpdate.forEach(item => {
        const assignedItemRef = doc(
          db, 
          'Tours', 
          selectedTour.id, 
          'tourDates', 
          selectedDate,
          'assignedInventoryItems',
          item.id
        );
        
        const updateData = action === 'checkIn' 
          ? {
              checkInStatus: {
                isCheckedIn: true,
                checkedInBy: userData.userId,
                checkedInByName: userName,
                checkedInAt: timestamp
              }
            }
          : {
              checkOutStatus: {
                isCheckedOut: true,
                checkedOutBy: userData.userId,
                checkedOutByName: userName,
                checkedOutAt: timestamp
              }
            };
            
        batch.update(assignedItemRef, updateData);
      });
      
      await batch.commit();
      
      // Update local state
      setAssignedInventory(prevInventory => 
        prevInventory.map(g => {
          if (g.groupId !== groupId) return g;
          
          const updatedItems = g.individualItems.map(item => {
            if ((action === 'checkIn' && item.checkInStatus?.isCheckedIn) ||
                (action === 'checkOut' && item.checkOutStatus?.isCheckedOut)) {
              // Skip items already checked
              return item;
            }
            
            if (action === 'checkIn') {
              return {
                ...item,
                checkInStatus: {
                  isCheckedIn: true,
                  checkedInBy: userData.userId,
                  checkedInByName: userName,
                  checkedInAt: timestamp
                }
              };
            } else {
              return {
                ...item,
                checkOutStatus: {
                  isCheckedOut: true,
                  checkedOutBy: userData.userId,
                  checkedOutByName: userName,
                  checkedOutAt: timestamp
                }
              };
            }
          });
          
          // Update counts
          const checkedInCount = updatedItems.filter(item => 
            item.checkInStatus && item.checkInStatus.isCheckedIn
          ).length;
          
          const checkedOutCount = updatedItems.filter(item => 
            item.checkOutStatus && item.checkOutStatus.isCheckedOut
          ).length;
          
          return {
            ...g,
            individualItems: updatedItems,
            quantityCheckedIn: checkedInCount,
            quantityCheckedOut: checkedOutCount,
            lastUpdated: timestamp
          };
        })
      );
  
      // Set success message
      setScanSuccessMessage(`Successfully ${action === 'checkIn' ? 'checked in' : 'checked out'} all available items in ${group.name}`);
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setScanSuccessMessage('');
      }, 3000);
      
      return true; // Return success
    } catch (error) {
      console.error(`Error marking all items as ${action === 'checkIn' ? 'checked in' : 'checked out'}:`, error);
      return false; // Return failure
    }
  };
  
  // Handle QR code scanning
  const handleQrScan = async (data, setSuccessMessage, setErrorMessage) => {
    // Special cases for mode changes
    if (data === 'CHANGE_MODE_INDIVIDUAL') {
      setScannerMode('individual');
      return;
    }
    
    if (data === 'CHANGE_MODE_MARKALL') {
      setScannerMode('markAll');
      return;
    }
    
    try {
      // Parse the QR code data
      let itemId;
      
      if (data.includes('/scan/')) {
        // Handle URL format: https://ontour.co.in/scan/{itemId}
        itemId = data.split('/scan/')[1];
      } else if (data.includes(':')) {
        // Handle direct format: {vendorId}:{groupId}:{itemId}
        const [, , id] = data.split(':');
        itemId = id;
      } else {
        // Try to use data directly as itemId
        itemId = data;
      }
      
      if (!itemId) {
        setErrorMessage('Invalid QR code format. Please try again.');
        return;
      }
      
      // Check if this item is in our assigned inventory
      const db = getFirestoreInstance();
      
      // First check if the item exists in the assignedInventoryItems subcollection for this tour date
      const assignedItemRef = doc(
        db,
        'Tours',
        selectedTour.id,
        'tourDates',
        selectedDate,
        'assignedInventoryItems',
        itemId
      );
      
      const assignedItemDoc = await getDoc(assignedItemRef);
      
      if (!assignedItemDoc.exists()) {
        setErrorMessage('This item is not assigned for this venue/date.');
        return;
      }
      
      const assignedItemData = assignedItemDoc.data();
      
      // Find the item's group in our local state
      let foundGroup = null;
      let foundItem = null;
      
      for (const group of assignedInventory) {
        if (group.groupId === assignedItemData.groupId) {
          foundGroup = group;
          foundItem = group.individualItems.find(i => i.id === itemId);
          break;
        }
      }
      
      if (!foundGroup) {
        // If we can't find the group in our local state, we need to fetch more data
        const groupDoc = await getDoc(doc(
          db,
          'Tours',
          selectedTour.id,
          'tourDates',
          selectedDate,
          'assignedInventoryGroups',
          assignedItemData.groupId
        ));
        
        if (!groupDoc.exists()) {
          setErrorMessage('Item group not found in assigned inventory.');
          return;
        }
        
        // We found the group but it wasn't loaded in our local state
        // This can happen if we're filtering by vendor
        if (vendorId && groupDoc.data().vendorId !== vendorId) {
          setErrorMessage('This item belongs to a different vendor.');
          return;
        }
        
        // In this case, we need to mark the item directly since we don't have it in local state
        if (scannerMode === 'individual') {
          // Check if the item is already checked in/out
          const isAlreadyChecked = checklistMode === 'checkIn' ? 
            assignedItemDoc.data()?.checkInStatus?.isCheckedIn : 
            assignedItemDoc.data()?.checkOutStatus?.isCheckedOut;
            
          if (isAlreadyChecked) {
            setErrorMessage(`This item is already ${checklistMode === 'checkIn' ? 'checked in' : 'checked out'}.`);
            return;
          }
          
          // Update item status in database
          const timestamp = Timestamp.now();
          
          await updateDoc(assignedItemRef, checklistMode === 'checkIn' 
            ? {
                checkInStatus: {
                  isCheckedIn: true,
                  checkedInBy: userData.userId,
                  checkedInByName: userData.fullName || 'Unknown User',
                  checkedInAt: timestamp
                }
              }
            : {
                checkOutStatus: {
                  isCheckedOut: true,
                  checkedOutBy: userData.userId,
                  checkedOutByName: userData.fullName || 'Unknown User',
                  checkedOutAt: timestamp
                }
              }
          );
          
          setSuccessMessage(`Successfully ${checklistMode === 'checkIn' ? 'checked in' : 'checked out'} ${assignedItemData.name || 'item'}`);
          return;
        } else {
          // For markAll mode on a group not in our local state, reload the data first
          setErrorMessage('Please switch to individual mode to scan this item.');
          return;
        }
      }
      
      // Check if the item is already checked in/out in individual mode
      if (scannerMode === 'individual') {
        const isAlreadyChecked = checklistMode === 'checkIn' ? 
          foundItem?.checkInStatus?.isCheckedIn : 
          foundItem?.checkOutStatus?.isCheckedOut;
          
        if (isAlreadyChecked) {
          setErrorMessage(`This item is already ${checklistMode === 'checkIn' ? 'checked in' : 'checked out'}.`);
          return;
        }
        
        const success = await handleMarkItem(foundGroup.groupId, itemId, checklistMode);
        
        if (success) {
          const itemName = foundItem?.serialNumber || assignedItemData.name || `Item`;
          setSuccessMessage(`Successfully ${checklistMode === 'checkIn' ? 'checked in' : 'checked out'} ${itemName}`);
        } else {
          setErrorMessage('Failed to update item status. Please try again.');
        }
      } else {
        // Mark all items in the group that are not already checked
        const success = await handleMarkAllItems(foundGroup.groupId, checklistMode);
        
        if (success) {
          setSuccessMessage(`Successfully ${checklistMode === 'checkIn' ? 'checked in' : 'checked out'} all available items in ${foundGroup.name}`);
        } else {
          setErrorMessage('Failed to update items or all items are already checked.');
        }
      }
    } catch (error) {
      console.error('Error processing QR code:', error);
      setErrorMessage(`Error: ${error.message}`);
    }
  };
  
  // Format timestamp for display
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    
    let date;
    try {
      // Handle different timestamp formats
      if (timestamp instanceof Date) {
        date = timestamp;
      } else if (timestamp.toDate && typeof timestamp.toDate === 'function') {
        // Firebase Timestamp object
        date = timestamp.toDate();
      } else if (timestamp.seconds && timestamp.nanoseconds) {
        // Firestore Timestamp object stored as plain object
        date = new Date(timestamp.seconds * 1000);
      } else {
        // Try parsing as string/number
        date = new Date(timestamp);
      }
      
      // Check if date is valid before formatting
      if (isNaN(date.getTime())) {
        return 'N/A';
      }
      
      return date.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
        hour12: true
      });
    } catch (e) {
      console.error('Error formatting timestamp:', e);
      return 'N/A';
    }
  };
  
  // Get user name for display
  const getUserName = (status) => {
    // First try to get user name
    if (status?.checkedInByName || status?.checkedOutByName) {
      return status.checkedInByName || status.checkedOutByName;
    }
    
    // If only userId is available, return that
    const userId = status?.checkedInBy || status?.checkedOutBy;
    return userId || 'N/A';
  };


  const determineUserForGroup = (group, mode) => {
    // Get all items that are checked in/out
    const items = group.individualItems.filter(item => 
      mode === 'checkIn' 
        ? item.checkInStatus?.isCheckedIn 
        : item.checkOutStatus?.isCheckedOut
    );
    
    if (items.length === 0) return 'N/A';
    
    // Get the user names
    const userNames = items.map(item => 
      mode === 'checkIn'
        ? (item.checkInStatus?.checkedInByName || 'Unknown')
        : (item.checkOutStatus?.checkedOutByName || 'Unknown')
    );
    
    // If all items were checked by the same user, show that name
    const allSameUser = userNames.every(name => name === userNames[0]);
    if (allSameUser) return userNames[0];
    
    // Otherwise show "Various"
    return 'Various Users';
  };
  
  // Handle inventory search
  const handleSearch = () => {
    if (!searchTerm.trim()) {
      setIsSearching(false);
      setSearchResults([]);
      return;
    }
    
    setIsSearching(true);
    
    const searchTermLower = searchTerm.toLowerCase();
    
    // Filter items that match the search term
    const results = assignedInventory.filter(item => {
      const nameMatch = item.name?.toLowerCase().includes(searchTermLower);
      const brandMatch = item.brand?.toLowerCase().includes(searchTermLower);
      const modelMatch = item.model?.toLowerCase().includes(searchTermLower);
      const keywordsMatch = item.keywords?.some(keyword => 
        keyword.toLowerCase().includes(searchTermLower)
      );
      
      return nameMatch || brandMatch || modelMatch || keywordsMatch;
    });
    
    setSearchResults(results);
  };
  
  // Handle navigation to item details
  const handleViewDetails = (item) => {
    navigate(`/tour/${selectedTour.id}/${selectedDate}/assigned-inventory/${item.groupId}`);
  };
  
  // Filter inventory by category
  const getFilteredInventory = () => {
    if (isSearching) {
      return searchResults;
    }
    
    if (activeCategory === 'all') {
      return assignedInventory;
    }
    
    return assignedInventory.filter(item => item.inventoryType === activeCategory);
  };

  // Handle changes to selected items for request
  const handleItemSelect = (item, quantity) => {
    if (quantity <= 0) {
      // Remove the item if quantity is 0 or negative
      setSelectedItems(prev => {
        const newItems = { ...prev };
        delete newItems[item.id];
        return newItems;
      });
    } else {
      // Add or update the item
      setSelectedItems(prev => ({
        ...prev,
        [item.id]: {
          item,
          quantity: Math.min(quantity, item.availableQuantity || 0)
        }
      }));
    }
  };

  // Handle quantity change for an item
  const handleQuantityChange = (item, delta) => {
    const currentQuantity = selectedItems[item.id]?.quantity || 0;
    const newQuantity = Math.max(0, currentQuantity + delta);
    handleItemSelect(item, newQuantity);
  };

  // Handle request submission
  const handleRequestSubmit = (item) => {
    setItemBeingRequested(item);
    setRequestNote('');
    setShowRequestNoteModal(true);
  };

  // Handle final request submission with notes
  const handleFinalRequestSubmit = async () => {
    if (!itemBeingRequested) return;
    
    try {
      setRequestSending(true);
      
      const db = getFirestoreInstance();
      const quantity = selectedItems[itemBeingRequested.id]?.quantity || 1;
      
      // Create a new request document
      const requestData = {
        groupId: itemBeingRequested.id,
        vendorId: itemBeingRequested.vendorId,
        requestedBy: userData.userId,
        requestedQuantity: quantity,
        notes: requestNote.trim(),
        tourId: selectedTour.id,
        tourDateId: selectedDate,
        status: 'pending',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        // Include item details for easy reference
        itemDetails: {
          name: itemBeingRequested.name,
          brand: itemBeingRequested.brand,
          model: itemBeingRequested.model || '',
          type: itemBeingRequested.type || '',
          subType: itemBeingRequested.subType || '',
          inventoryType: itemBeingRequested.inventoryType || '',
          coverPhotoUrl: itemBeingRequested.coverPhotoUrl || null
        },
        // Include requester details
        requesterDetails: {
          fullName: userData.fullName || '',
          userName: userData.userName || '',
          profilePictureUrl: userData.profilePictureUrl || '',
          userType: userData.userType || ''
        }
      };
      
      // Add to inventoryRequests subcollection
      const requestRef = await addDoc(
        collection(db, 'Tours', selectedTour.id, 'tourDates', selectedDate, 'inventoryRequests'),
        requestData
      );
      
      // Send notification to vendor
      await createNotification(itemBeingRequested.vendorId, 'general', {
        type: 'INVENTORY_REQUEST',
        title: 'New Inventory Request',
        message: `${userData.fullName || 'A user'} has requested ${quantity} ${itemBeingRequested.name} for ${selectedTour.tourName}`,
        data: {
          requestId: requestRef.id,
          tourId: selectedTour.id,
          tourDateId: selectedDate,
          itemId: itemBeingRequested.id,
          requestedBy: {
            userId: userData.userId,
            fullName: userData.fullName || '',
            userType: userData.userType || ''
          },
          _displayData: {
            tourName: selectedTour.tourName || '',
            itemName: itemBeingRequested.name || '',
            quantity: quantity
          }
        }
      });
      
      // Clear the item from selected items
      setSelectedItems(prev => {
        const newItems = { ...prev };
        delete newItems[itemBeingRequested.id];
        return newItems;
      });
      
      // Show success message
      setRequestSuccessMessage(`Request for ${quantity} ${itemBeingRequested.name} sent successfully!`);
      setShowRequestSuccessMessage(true);
      
      // Hide after 3 seconds
      setTimeout(() => {
        setShowRequestSuccessMessage(false);
      }, 3000);
      
      // Close the modal
      setShowRequestNoteModal(false);
      setItemBeingRequested(null);
      
      // Refresh the requests data
      fetchExistingRequests();
      
    } catch (error) {
      console.error('Error sending inventory request:', error);
      alert('Failed to send request. Please try again.');
    } finally {
      setRequestSending(false);
    }
  };

  // Handle request approval
  const handleApproveRequest = async (request) => {
    if (!request || !userData?.userId) return;
    
    try {
      setIsLoadingRequests(true);
      
      const db = getFirestoreInstance();
      
      // Step 1: Update the request status
      const requestRef = doc(
        db, 
        'Tours', 
        selectedTour.id, 
        'tourDates', 
        selectedDate, 
        'inventoryRequests', 
        request.id
      );
      
      await updateDoc(requestRef, {
        status: 'approved',
        approvedBy: userData.userId,
        approvedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        approverDetails: {
          fullName: userData.fullName || '',
          userName: userData.userName || '',
          profilePictureUrl: userData.profilePictureUrl || '',
          userType: userData.userType || ''
        }
      });
      
      // Step 2: Assign the inventory
      // Get available items for this group
      const availableItemsQuery = query(
        collection(db, 'users', request.vendorId, 'inventoryItems'),
        where('groupId', '==', request.groupId),
        where('status', '==', 'Available'),
        limit(request.requestedQuantity)
      );
      
      const availableItemsSnap = await getDocs(availableItemsQuery);
      
      if (availableItemsSnap.docs.length < request.requestedQuantity) {
        throw new Error('Not enough available items to fulfill the request');
      }
      
      // Get the group data
      const groupRef = doc(db, 'users', request.vendorId, 'inventoryGroups', request.groupId);
      const groupSnap = await getDoc(groupRef);
      
      if (!groupSnap.exists()) {
        throw new Error('Inventory group not found');
      }
      
      const groupData = groupSnap.data();
      
      // Use a batch for all writes
      const batch = writeBatch(db);
      
      // Update group document with new counts
      batch.update(groupRef, {
        availableQuantity: Math.max(0, (groupData.availableQuantity || 0) - request.requestedQuantity),
        reservedQuantity: (groupData.reservedQuantity || 0) + request.requestedQuantity,
        updatedAt: serverTimestamp()
      });
      
      // Create assignedInventoryGroups entry
      const assignedGroupRef = doc(
        db,
        'Tours',
        selectedTour.id,
        'tourDates',
        selectedDate,
        'assignedInventoryGroups',
        request.groupId
      );
      
      // Check if group is already assigned
      const existingGroupSnap = await getDoc(assignedGroupRef);
      
      if (existingGroupSnap.exists()) {
        // Update existing group with increased quantity
        const existingData = existingGroupSnap.data();
        batch.update(assignedGroupRef, {
          totalQuantity: (existingData.totalQuantity || 0) + request.requestedQuantity,
          updatedAt: serverTimestamp()
        });
      } else {
        // Create new group entry
        batch.set(assignedGroupRef, {
          groupId: request.groupId,
          vendorId: request.vendorId,
          inventoryType: groupData.inventoryType || request.itemDetails?.inventoryType || '',
          type: groupData.type || request.itemDetails?.type || '',
          subType: groupData.subType || request.itemDetails?.subType || '',
          brand: groupData.brand || request.itemDetails?.brand || '',
          model: groupData.model || request.itemDetails?.model || '',
          name: groupData.name || request.itemDetails?.name || '',
          description: groupData.description || '',
          rentPerDay: groupData.rentPerDay || 0,
          coverPhotoUrl: groupData.coverPhotoUrl || request.itemDetails?.coverPhotoUrl || null,
          fileUrl: groupData.fileUrl || null,
          totalQuantity: request.requestedQuantity,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
      }
      
      // Process each individual item
      const itemsToAssign = availableItemsSnap.docs;
      const assignedItemIds = [];
      
      for (const itemDoc of itemsToAssign) {
        const itemId = itemDoc.id;
        const itemData = itemDoc.data();
        
        // Update the item status in vendor's inventory
        const itemRef = doc(db, 'users', request.vendorId, 'inventoryItems', itemId);
        batch.update(itemRef, {
          status: 'Reserved',
          updatedAt: serverTimestamp(),
          assignedTourIds: {
            [selectedTour.id]: {
              tourId: selectedTour.id,
              tourName: selectedTour.tourName || '',
              actName: selectedTour.actDetails?.name || '',
              assignedDates: [{
                tourDateDocId: selectedDate,
                date: new Date().toISOString() // Use the current date as a fallback
              }],
              assignedBy: userData.userId,
              assignedAt: serverTimestamp()
            }
          }
        });
        
        // Add to assignedInventoryItems subcollection
        const assignedItemRef = doc(
          db,
          'Tours',
          selectedTour.id,
          'tourDates',
          selectedDate,
          'assignedInventoryItems',
          itemId
        );
        
        batch.set(assignedItemRef, {
          id: itemId,
          name: groupData.name || request.itemDetails?.name || '',
          brand: groupData.brand || request.itemDetails?.brand || '',
          model: itemData.model || groupData.model || request.itemDetails?.model || '',
          serialNumber: itemData.serialNumber || '',
          type: groupData.type || request.itemDetails?.type || '',
          subType: groupData.subType || request.itemDetails?.subType || '',
          rentPerDay: groupData.rentPerDay || 0,
          groupId: request.groupId,
          vendorId: request.vendorId,
          assignedBy: userData.userId,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
        
        assignedItemIds.push(itemId);
      }
      
      // Commit all changes
      await batch.commit();
      
      // Send notification to requester
      await createNotification(request.requestedBy, 'general', {
        type: 'INVENTORY_REQUEST_APPROVED',
        title: 'Inventory Request Approved',
        message: `Your request for ${request.requestedQuantity} ${request.itemDetails?.name || 'items'} has been approved`,
        data: {
          requestId: request.id,
          tourId: selectedTour.id,
          tourDateId: selectedDate,
          itemId: request.groupId,
          approvedBy: {
            userId: userData.userId,
            fullName: userData.fullName || '',
            userType: userData.userType || ''
          },
          _displayData: {
            tourName: selectedTour.tourName || '',
            itemName: request.itemDetails?.name || '',
            quantity: request.requestedQuantity
          }
        }
      });
      
      // Remove from pending requests
      setPendingRequests(prev => prev.filter(r => r.id !== request.id));
      
      // Refresh inventory data
      if (viewMode === 'inventory') {
        const currentCategory = activeCategory;
        setActiveCategory(currentCategory);
      }
      
      alert(`Request for ${request.requestedQuantity} ${request.itemDetails?.name || 'items'} approved successfully!`);
    } catch (error) {
      console.error('Error approving inventory request:', error);
      alert(`Failed to approve request: ${error.message}`);
    } finally {
      setIsLoadingRequests(false);
    }
  };

  // Handle request rejection
  const handleRejectRequest = async (request) => {
    if (!request || !userData?.userId) return;
    
    try {
      setIsLoadingRequests(true);
      
      const db = getFirestoreInstance();
      
      // Update the request status
      const requestRef = doc(
        db, 
        'Tours', 
        selectedTour.id, 
        'tourDates', 
        selectedDate, 
        'inventoryRequests', 
        request.id
      );
      
      await updateDoc(requestRef, {
        status: 'rejected',
        rejectedBy: userData.userId,
        rejectedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        rejectorDetails: {
          fullName: userData.fullName || '',
          userName: userData.userName || '',
          profilePictureUrl: userData.profilePictureUrl || '',
          userType: userData.userType || ''
        }
      });
      
      // Send notification to requester
      await createNotification(request.requestedBy, 'general', {
        type: 'INVENTORY_REQUEST_REJECTED',
        title: 'Inventory Request Rejected',
        message: `Your request for ${request.requestedQuantity} ${request.itemDetails?.name || 'items'} has been rejected`,
        data: {
          requestId: request.id,
          tourId: selectedTour.id,
          tourDateId: selectedDate,
          itemId: request.groupId,
          rejectedBy: {
            userId: userData.userId,
            fullName: userData.fullName || '',
            userType: userData.userType || ''
          },
          _displayData: {
            tourName: selectedTour.tourName || '',
            itemName: request.itemDetails?.name || '',
            quantity: request.requestedQuantity
          }
        }
      });
      
      // Remove from pending requests
      setPendingRequests(prev => prev.filter(r => r.id !== request.id));
      
      alert(`Request for ${request.requestedQuantity} ${request.itemDetails?.name || 'items'} rejected.`);
    } catch (error) {
      console.error('Error rejecting inventory request:', error);
      alert(`Failed to reject request: ${error.message}`);
    } finally {
      setIsLoadingRequests(false);
    }
  };
  
  // Render inventory cards view
  const renderInventoryView = () => {
    const filteredItems = getFilteredInventory();
    
    if (filteredItems.length === 0 && !isLoading && !isSearching) {
      return (
        <div className={styles.emptyState}>
          <p>No inventory has been assigned for this event in the {activeCategory} category.</p>
        </div>
      );
    }
    
    // Determine if user can see detailed information
    const canViewDetailedInfo = userData?.userType === 'Vendor' || userData?.userType === 'Staff';
    
    return (
      <>
        <div className={styles.searchContainer}>
          <div className={styles.searchBar}>
            <input 
              type="text" 
              placeholder="Search inventory..." 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleSearch();
                }
              }}
              className={styles.searchInput}
            />
            <button 
              className={styles.searchButton}
              onClick={handleSearch}
            >
              <FontAwesomeIcon icon={faSearch} />
            </button>
          </div>
          
          <div className={styles.categoryFilter}>
            <div className={styles.categoryButtons}>
              {categories.map(category => (
                <button
                  key={category}
                  className={`${styles.categoryButton} ${activeCategory === category ? styles.activeCategory : ''}`}
                  onClick={() => {
                    setActiveCategory(category);
                    setCurrentPage(1); // Reset to first page when changing category
                    setIsSearching(false); // Clear any search results
                    setSearchTerm(''); // Clear search term
                  }}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>
        </div>
        
        {isLoading ? (
          <div className={styles.loadingContainer}>
            <FontAwesomeIcon icon={faSpinner} spin />
            <p>Loading assigned inventory...</p>
          </div>
        ) : isSearching && searchResults.length === 0 ? (
          <div className={styles.emptyState}>
            <p>No results found for "{searchTerm}"</p>
          </div>
        ) : (
          <>
            <div className={styles.cardGrid}>
              {(isSearching ? searchResults : filteredItems).map((item) => (
                <div key={item.groupId} className={styles.equipmentCard} onClick={() => handleViewDetails(item)}>
                  <div className={styles.cardImageContainer}>
                    {item.coverPhotoUrl ? (
                      <img 
                        src={item.coverPhotoUrl} 
                        alt={item.name} 
                        className={styles.cardImage}
                      />
                    ) : (
                      <div className={styles.defaultImage}>
                        <span>{item.name.charAt(0)}</span>
                      </div>
                    )}
                  </div>
                  
                  <div className={styles.cardContent}>
                    <p className={styles.cardCategory}>
                      {item.inventoryType || 'Equipment'}
                    </p>
                    <h3 className={styles.cardTitle}>
                      {item.name}
                    </h3>
                    
                    <p className={styles.cardBrand}>
                      {item.brand} {item.model && `- ${item.model}`}
                      {!item.model && item.subType && ` - ${item.subType}`}
                    </p>
  
                    <div className={styles.cardDetails}>
                      <span>
                        Qty: {item.assignedQuantity || item.individualItems?.length || 0}
                      </span>
                      {item.rentPerDay && <span>₹{item.rentPerDay}/day</span>}
                    </div>
                    
                    {item.description && (
                      <p className={styles.cardDescription}>{item.description}</p>
                    )}
                    
                    {canViewDetailedInfo && (
                      <>
                        {/* Check-in/out status indicators */}
                        <div className={styles.statusIndicators}>
                          <span className={`${styles.statusBadge} ${styles.checkedInBadge}`}>
                            {item.quantityCheckedIn || 0} Checked In
                          </span>
                          <span className={`${styles.statusBadge} ${styles.checkedOutBadge}`}>
                            {item.quantityCheckedOut || 0} Checked Out
                          </span>
                        </div>
                        
                        {item.lastUpdated && (
                          <div className={styles.lastUpdated}>
                            Last Updated: {formatTimestamp(item.lastUpdated)}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
            
            {/* Show load more button if not searching and there are more items */}
            {!isSearching && hasMoreItems && (
              <div className={styles.loadMoreContainer}>
                <button 
                  className={styles.loadMoreButton}
                  onClick={handleLoadMore}
                >
                  Load More
                </button>
              </div>
            )}
          </>
        )}
      </>
    );
  };
  
  // Render checklist table view
  const renderChecklistView = () => {
    // Filter inventory items based on category
    const filteredItems = getFilteredInventory();
    
    return (
      <div className={styles.checklistContainer}>
      
      <div className={styles.checklistHeader}>
  <div className={styles.checklistActions}>
    <button 
      className={`${styles.modeButton} ${checklistMode === 'checkIn' ? styles.activeMode : ''}`}
      onClick={() => setChecklistMode('checkIn')}
    >
      <FontAwesomeIcon icon={faSignInAlt} />
      <span>Check-In Mode</span>
    </button>
    <button 
      className={`${styles.modeButton} ${checklistMode === 'checkOut' ? styles.activeMode : ''}`}
      onClick={() => setChecklistMode('checkOut')}
    >
      <FontAwesomeIcon icon={faSignOutAlt} />
      <span>Check-Out Mode</span>
    </button>
  </div>
  
  <div className={styles.headerActions}>
    <button 
      className={styles.scanButton}
      onClick={() => setShowScanner(true)}
    >
      <FontAwesomeIcon icon={faQrcode} />
      <span>Scan By QR Code</span>
    </button>
    
    <button 
      className={styles.printButton}
      onClick={handleExportChecklist}
    >
      <FontAwesomeIcon icon={faShare} />
      <span>Print Checklist</span>
    </button>
  </div>
</div>
        
        {scanSuccessMessage && (
          <div className={styles.successMessage}>
            <FontAwesomeIcon icon={faCheckCircle} />
            <span>{scanSuccessMessage}</span>
          </div>
        )}
        
        <div className={styles.checklistTable}>
          {isMobile ? (
            // Mobile-optimized headers
            <div className={styles.mobileTableHeader}>
              <div className={styles.itemNameColumn}>Item</div>
              <div className={styles.quantityColumn}>Qty</div>
              <div className={styles.checkboxColumn}>
                {checklistMode === 'checkIn' ? 'In' : 'Out'}
              </div>
              <div className={styles.timestampColumn}>At</div>
              <div className={styles.userColumn}>By</div>
            </div>
          ) : (
            // Desktop headers
            <div className={styles.tableHeader}>
              <div className={styles.itemNameColumn}>Item Name</div>
              <div className={styles.quantityColumn}>Qty Assigned</div>
              <div className={styles.checkboxColumn}>
                {checklistMode === 'checkIn' ? 'Check In' : 'Check Out'}
              </div>
              <div className={styles.timestampColumn}>
                {checklistMode === 'checkIn' ? 'Checked In At' : 'Checked Out At'}
              </div>
              <div className={styles.userColumn}>
                {checklistMode === 'checkIn' ? 'Checked In By' : 'Checked Out By'}
              </div>
            </div>
          )}
          
          <div className={styles.tableBody}>
            {filteredItems.map((group) => (
              <React.Fragment key={group.groupId}>
                <div 
                  className={styles.groupRow}
                  onClick={() => toggleItemExpansion(group.groupId)}
                >
                  <div className={styles.itemNameColumn}>
                    <FontAwesomeIcon 
                      icon={expandedItems[group.groupId] ? faChevronDown : faChevronRight} 
                      className={styles.expandIcon}
                    />
                    {group.name}
                  </div>
                  <div className={styles.quantityColumn}>
                    {group.assignedQuantity || group.individualItems?.length || 0}
                  </div>
                  <div className={styles.checkboxColumn}>
                    <label className={styles.markAllLabel}>
                      <input
                        type="checkbox"
                        checked={
                          checklistMode === 'checkIn' 
                            ? (group.quantityCheckedIn || 0) === (group.assignedQuantity || group.individualItems?.length || 0)
                            : (group.quantityCheckedOut || 0) === (group.assignedQuantity || group.individualItems?.length || 0)
                        }
                        onChange={() => handleMarkAllItems(
                          group.groupId, 
                          checklistMode
                        )}
                        disabled={
                          checklistMode === 'checkIn' 
                            ? (group.quantityCheckedIn || 0) === (group.assignedQuantity || group.individualItems?.length || 0)
                            : (group.quantityCheckedOut || 0) === (group.assignedQuantity || group.individualItems?.length || 0)
                        }
                      />
                      <span>Mark All</span>
                    </label>
                  </div>
                  <div className={styles.timestampColumn}>
  {(() => {
    // For group rows, find the latest timestamp among checked items
    if (checklistMode === 'checkIn') {
      const checkedItems = group.individualItems.filter(item => 
        item.checkInStatus?.isCheckedIn
      );
      
      if (checkedItems.length === 0) return 'N/A';
      
      // Find the most recent timestamp
      const latestTimestamp = checkedItems.reduce((latest, item) => {
        const current = item.checkInStatus?.checkedInAt;
        if (!current) return latest;
        
        if (!latest) return current;
        
        // Convert both to Date objects for comparison
        const currentDate = current instanceof Date ? current : 
                           current.toDate ? current.toDate() : 
                           new Date(current);
        
        const latestDate = latest instanceof Date ? latest : 
                          latest.toDate ? latest.toDate() : 
                          new Date(latest);
        
        return currentDate > latestDate ? current : latest;
      }, null);
      
      return latestTimestamp ? formatTimestamp(latestTimestamp) : 'N/A';
    } else {
      // For checkout mode
      const checkedItems = group.individualItems.filter(item => 
        item.checkOutStatus?.isCheckedOut
      );
      
      if (checkedItems.length === 0) return 'N/A';
      
      // Find the most recent timestamp
      const latestTimestamp = checkedItems.reduce((latest, item) => {
        const current = item.checkOutStatus?.checkedOutAt;
        if (!current) return latest;
        
        if (!latest) return current;
        
        // Convert both to Date objects for comparison
        const currentDate = current instanceof Date ? current : 
                           current.toDate ? current.toDate() : 
                           new Date(current);
        
        const latestDate = latest instanceof Date ? latest : 
                          latest.toDate ? latest.toDate() : 
                          new Date(latest);
        
        return currentDate > latestDate ? current : latest;
      }, null);
      
      return latestTimestamp ? formatTimestamp(latestTimestamp) : 'N/A';
    }
  })()}
</div>
             <div className={styles.userColumn}>
                    {/* THIS IS THE UPDATED CODE FOR USER DISPLAY */}
                    {(() => {
                      // For groups, determine if all checked items were done by the same user
                      const items = group.individualItems.filter(item => 
                        checklistMode === 'checkIn' 
                          ? item.checkInStatus?.isCheckedIn 
                          : item.checkOutStatus?.isCheckedOut
                      );
                      
                      if (items.length === 0) return 'N/A';
                      
                      // Get the user names
                      const userNames = items.map(item => 
                        checklistMode === 'checkIn'
                          ? (item.checkInStatus?.checkedInByName || 'Unknown')
                          : (item.checkOutStatus?.checkedOutByName || 'Unknown')
                      );
                      
                      // If all items were checked by the same user, show that name
                      const allSameUser = userNames.every(name => name === userNames[0]);
                      if (allSameUser) return userNames[0];
                      
                      // Otherwise show "Various Users"
                      return 'Various Users';
                    })()}
                  </div>
                </div>
                
                
                {/* Individual items rows (expanded) */}
                {expandedItems[group.groupId] && group.individualItems?.map((item) => (
                  <div key={item.id} className={styles.itemRow}>
                    <div className={styles.itemNameColumn}>
                      <span className={styles.indentedItem}>
                        {item.serialNumber || `Unit ${item.id.slice(-4)}`}
                      </span>
                    </div>
                    <div className={styles.quantityColumn}>
                      1
                    </div>
                    <div className={styles.checkboxColumn}>
                      <input
                        type="checkbox"
                        checked={
                          checklistMode === 'checkIn' 
                            ? (item.checkInStatus?.isCheckedIn || false)
                            : (item.checkOutStatus?.isCheckedOut || false)
                        }
                        onChange={() => handleMarkItem(
                          group.groupId,
                          item.id,
                          checklistMode
                        )}
                        disabled={
                          checklistMode === 'checkIn' 
                            ? (item.checkInStatus?.isCheckedIn || false)
                            : (item.checkOutStatus?.isCheckedOut || false)
                        }
                      />
                    </div>
                    <div className={styles.timestampColumn}>
                      {checklistMode === 'checkIn' 
                        ? (item.checkInStatus?.checkedInAt ? formatTimestamp(item.checkInStatus.checkedInAt) : 'N/A')
                        : (item.checkOutStatus?.checkedOutAt ? formatTimestamp(item.checkOutStatus.checkedOutAt) : 'N/A')
                      }
                    </div>
                    <div className={styles.userColumn}>
                      {checklistMode === 'checkIn'
                        ? getUserName(item.checkInStatus)
                        : getUserName(item.checkOutStatus)
                      }
                    </div>
                  </div>
                ))}
              </React.Fragment>
            ))}
          </div>
        </div>
        
        {/* Optional scroll indicator for mobile */}
        {isMobile && (
          <div className={styles.horizontalScrollIndicator}>
            <FontAwesomeIcon icon={faArrowsAltH} size="xs" />
            Scroll
          </div>
        )}
      </div>
    );
  };

  // Render the request view (for non-vendor/staff users)
  const renderRequestView = () => {
    if (noVendorSelected) {
      return (
        <div className={styles.emptyState}>
          <p>Please select a vendor to view and request their inventory.</p>
        </div>
      );
    }
    
    if (isLoading) {
      return (
        <div className={styles.loadingContainer}>
          <FontAwesomeIcon icon={faSpinner} spin />
          <p>Loading available inventory...</p>
        </div>
      );
    }
    
    if (requestableInventory.length === 0) {
      return (
        <div className={styles.emptyState}>
          <p>No inventory is available for request from this vendor in the {activeCategory} category.</p>
        </div>
      );
    }
    
    return (
      <>
        <div className={styles.searchContainer}>
          <div className={styles.searchBar}>
            <input 
              type="text" 
              placeholder="Search available inventory..." 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleSearch();
                }
              }}
              className={styles.searchInput}
            />
            <button 
              className={styles.searchButton}
              onClick={handleSearch}
            >
              <FontAwesomeIcon icon={faSearch} />
            </button>
          </div>
          
          <div className={styles.categoryFilter}>
            <div className={styles.categoryButtons}>
              {categories.map(category => (
                <button
                  key={category}
                  className={`${styles.categoryButton} ${activeCategory === category ? styles.activeCategory : ''}`}
                  onClick={() => {
                    setActiveCategory(category);
                    setCurrentPage(1);
                    setIsSearching(false);
                    setSearchTerm('');
                  }}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className={styles.cardGrid}>
          {requestableInventory.map((item) => (
            <div key={item.id} className={styles.equipmentCard}>
              <div className={styles.cardImageContainer}>
                {item.coverPhotoUrl ? (
                  <img 
                    src={item.coverPhotoUrl} 
                    alt={item.name} 
                    className={styles.cardImage}
                  />
                ) : (
                  <div className={styles.defaultImage}>
                    <span>{item.name.charAt(0)}</span>
                  </div>
                )}
                {item.isAssigned && (
                  <div className={styles.assignedTag}>
                    Already Assigned
                  </div>
                )}
              </div>
              
              <div className={styles.cardContent}>
                <p className={styles.cardCategory}>
                  {item.inventoryType || 'Equipment'}
                </p>
                <h3 className={styles.cardTitle}>
                  {item.name}
                </h3>
                
                <p className={styles.cardBrand}>
                  {item.brand} {item.model && `- ${item.model}`}
                  {!item.model && item.subType && ` - ${item.subType}`}
                </p>

                <div className={styles.cardDetails}>
                  <span>
                    Available: {item.availableQuantity || 0}
                  </span>
                  {item.rentPerDay && <span>₹{item.rentPerDay}/day</span>}
                </div>
                
                {item.description && (
                  <p className={styles.cardDescription}>{item.description}</p>
                )}
                
                {/* Render Pending Requests Info */}
                {pendingItemRequests[item.id] && pendingItemRequests[item.id].length > 0 && (
                  <div className={`${styles.requestInfo} ${styles.pendingRequests}`}>
                    <h4 className={styles.requestInfoTitle}>Already Requested</h4>
                    {pendingItemRequests[item.id].map(request => (
                      <div key={request.id} className={styles.requestEntry}>
                        <span className={styles.requesterName}>{request.requesterName}</span>
                        <span className={styles.requestQuantity}>Qty: {request.quantity}</span>
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Render Accepted Requests Info */}
                {acceptedItemRequests[item.id] && acceptedItemRequests[item.id].length > 0 && (
                  <div className={`${styles.requestInfo} ${styles.approvedRequests}`}>
                    <h4 className={styles.requestInfoTitle}>Approved Requests</h4>
                    {acceptedItemRequests[item.id].map(request => (
                      <div key={request.id} className={styles.requestEntry}>
                        <span className={styles.requesterName}>{request.requesterName}</span>
                        <span className={styles.requestQuantity}>Qty: {request.quantity}</span>
                      </div>
                    ))}
                  </div>
                )}
                
                <div className={styles.requestControls}>
                  <div className={styles.quantitySelector}>
                    <button 
                      className={styles.quantityButton}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleQuantityChange(item, -1);
                      }}
                      disabled={!selectedItems[item.id] || selectedItems[item.id].quantity <= 0}
                    >
                      <FontAwesomeIcon icon={faMinus} />
                    </button>
                    <span className={styles.quantityValue}>
                      {selectedItems[item.id]?.quantity || 0}
                    </span>
                    <button 
                      className={styles.quantityButton}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleQuantityChange(item, 1);
                      }}
                      disabled={(selectedItems[item.id]?.quantity || 0) >= item.availableQuantity}
                    >
                      <FontAwesomeIcon icon={faPlus} />
                    </button>
                  </div>
                  
                  <button 
                    className={styles.requestButton}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRequestSubmit(item);
                    }}
                    disabled={(selectedItems[item.id]?.quantity || 0) <= 0}
                  >
                    <FontAwesomeIcon icon={faHandHolding} />
                    <span>Request</span>
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Request Note Modal */}
        {showRequestNoteModal && (
          <div className={styles.modalOverlay} onClick={() => setShowRequestNoteModal(false)}>
            <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
              <h3 className={styles.modalTitle}>Additional Request Notes</h3>
              <p className={styles.modalDescription}>
                Add any additional notes for your request of {selectedItems[itemBeingRequested?.id]?.quantity || 1} {itemBeingRequested?.name}
              </p>
              
              <textarea
                className={styles.noteTextarea}
                placeholder="Add any specific requirements or notes for this request..."
                value={requestNote}
                onChange={(e) => setRequestNote(e.target.value)}
                rows={5}
              />
              
              <div className={styles.modalActions}>
                <button 
                  className={styles.cancelButton}
                  onClick={() => setShowRequestNoteModal(false)}
                  disabled={requestSending}
                >
                  Cancel
                </button>
                <button 
                  className={styles.submitButton}
                  onClick={handleFinalRequestSubmit}
                  disabled={requestSending}
                >
                  {requestSending ? (
                    <>
                      <FontAwesomeIcon icon={faSpinner} spin />
                      <span>Sending...</span>
                    </>
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faPaperPlane} />
                      <span>Send Request</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Success Message */}
        {showRequestSuccessMessage && (
          <div className={styles.successMessageOverlay}>
            <div className={styles.successMessageContent}>
              <FontAwesomeIcon icon={faCheckCircle} className={styles.successIcon} />
              <p>{requestSuccessMessage}</p>
            </div>
          </div>
        )}
      </>
    );
  };

  // Render the requests view (for vendor/staff to manage requests)
  const renderRequestsView = () => {
    if (isLoadingRequests) {
      return (
        <div className={styles.loadingContainer}>
          <FontAwesomeIcon icon={faSpinner} spin />
          <p>Loading inventory requests...</p>
        </div>
      );
    }
    
    if (pendingRequests.length === 0) {
      return (
        <div className={styles.emptyState}>
          <p>No pending inventory requests for this event.</p>
        </div>
      );
    }
    
    return (
      <div className={styles.requestsContainer}>
        <h3 className={styles.requestsTitle}>Pending Inventory Requests</h3>
        
        <div className={styles.requestsList}>
          {pendingRequests.map(request => (
            <div key={request.id} className={styles.requestCard}>
              <div className={styles.requestHeader}>
                <div className={styles.requestUserInfo}>
                  {request.requesterDetails?.profilePictureUrl ? (
                    <img 
                      src={request.requesterDetails.profilePictureUrl} 
                      alt={request.requesterDetails.fullName} 
                      className={styles.requesterPhoto}
                    />
                  ) : (
                    <div className={styles.defaultUserPhoto}>
                      {request.requesterDetails?.fullName?.charAt(0) || '?'}
                    </div>
                  )}
                  <div className={styles.requesterDetails}>
                    <h4 className={styles.requesterName}>
                      {request.requesterDetails?.fullName || 'Unknown User'}
                    </h4>
                    <p className={styles.requesterType}>
                      {request.requesterDetails?.userType || 'User'}
                      {request.requesterDetails?.userName && ` • @${request.requesterDetails.userName}`}
                    </p>
                  </div>
                </div>
                <div className={styles.requestDate}>
                  {formatTimestamp(request.createdAt)}
                </div>
              </div>
              
              <div className={styles.requestDetails}>
                <div className={styles.requestItemInfo}>
                  {request.itemDetails?.coverPhotoUrl ? (
                    <img 
                      src={request.itemDetails.coverPhotoUrl} 
                      alt={request.itemDetails.name} 
                      className={styles.requestItemImage}
                    />
                  ) : (
                    <div className={styles.defaultItemImage}>
                      {request.itemDetails?.name?.charAt(0) || 'I'}
                    </div>
                  )}
                  <div className={styles.requestItemDetails}>
                    <h4 className={styles.requestItemName}>
                      {request.itemDetails?.name || 'Unknown Item'}
                    </h4>
                    <p className={styles.requestItemBrand}>
                      {request.itemDetails?.brand || ''} 
                      {request.itemDetails?.model && ` - ${request.itemDetails.model}`}
                    </p>
                    <div className={styles.requestQuantity}>
                      <span className={styles.quantityLabel}>Requested:</span>
                      <span className={styles.quantityValue}>{request.requestedQuantity}</span>
                    </div>
                  </div>
                </div>
                
                {request.notes && (
                  <div className={styles.requestNotes}>
                    <h5 className={styles.notesLabel}>Notes:</h5>
                    <p className={styles.notesContent}>{request.notes}</p>
                  </div>
                )}
                
                <div className={styles.requestActions}>
                  <button 
                    className={styles.approveButton}
                    onClick={() => handleApproveRequest(request)}
                    disabled={isLoadingRequests}
                  >
                    <FontAwesomeIcon icon={faCheck} />
                    <span>Approve</span>
                  </button>
                  <button 
                    className={styles.rejectButton}
                    onClick={() => handleRejectRequest(request)}
                    disabled={isLoadingRequests}
                  >
                    <FontAwesomeIcon icon={faBan} />
                    <span>Reject</span>
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };
  
  if (isLoading && viewMode === 'inventory') {
    return (
      <div className={styles.loadingContainer}>
        <FontAwesomeIcon icon={faSpinner} spin className={styles.spinner} />
        <p>Loading assigned inventory...</p>
      </div>
    );
  }
  
  return (
    <div className={styles.assignedInventoryContainer}>
      <div className={styles.header}>
        <button className={styles.backButton} onClick={handleBack}>
          <FontAwesomeIcon icon={faChevronLeft} />
          Back
        </button>
        <h2 className={styles.title}>Assigned Inventory</h2>
        
        <div className={styles.viewSelector}>
          <button
            className={`${styles.viewButton} ${viewMode === 'inventory' ? styles.activeView : ''}`}
            onClick={() => setViewMode('inventory')}
          >
            <FontAwesomeIcon icon={faList} />
            <span>Inventory View</span>
          </button>
          
          {showChecklistTab && (
            <button
              className={`${styles.viewButton} ${viewMode === 'checklist' ? styles.activeView : ''}`}
              onClick={() => setViewMode('checklist')}
            >
              <FontAwesomeIcon icon={faClipboardCheck} />
              <span>Checklist</span>
            </button>
          )}
          
          {showRequestTab && (
            <button
              className={`${styles.viewButton} ${viewMode === 'request' ? styles.activeView : ''}`}
              onClick={() => setViewMode('request')}
            >
              <FontAwesomeIcon icon={faHandHolding} />
              <span>Request</span>
            </button>
          )}
          
          {showRequestsTab && (
            <button
              className={`${styles.viewButton} ${viewMode === 'requests' ? styles.activeView : ''}`}
              onClick={() => setViewMode('requests')}
            >
              <FontAwesomeIcon icon={faInbox} />
              <span>Requests</span>
            </button>
          )}
        </div>
      </div>
      
      {viewMode === 'inventory' && renderInventoryView()}
      {viewMode === 'checklist' && showChecklistTab && renderChecklistView()}
      {viewMode === 'request' && showRequestTab && renderRequestView()}
      {viewMode === 'requests' && showRequestsTab && renderRequestsView()}

      {showPdfModal && (
  <ChecklistPdfModal
    isOpen={showPdfModal}
    onClose={() => setShowPdfModal(false)}
    pdfHtml={pdfHtml}
  />
)}

      {/* QR Scanner Component */}
      <QrScanner 
        isOpen={showScanner}
        onClose={() => setShowScanner(false)}
        onScan={handleQrScan}
        mode={scannerMode}
        checklistMode={checklistMode}
        keepOpenAfterScan={true}
      />
    </div>
  );
};

export default AssignedInventoryTab;